import React, { forwardRef, HTMLAttributes } from 'react';
import classNames from 'classnames';
import styles from './TreeItem.module.css';
import { Handle } from './HandleComponent';

import { DraftManual, FlattenedItem } from 'views/App/Portal/Policy/_types/manuals/draft';
import { Box, ListItem, Typography, IconButton } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';
import { useConfirm } from 'material-ui-confirm';
import { translate as t } from 'hooks/useTranslations';

export interface Props extends HTMLAttributes<HTMLLIElement> {
  childCount?: number;
  clone?: boolean;
  collapsed?: boolean;
  depth: number;
  disableInteraction?: boolean;
  disableSelection?: boolean;
  ghost?: boolean;
  handleProps?: any;
  indicator?: boolean;
  indentationWidth: number;
  item: any;
  value: string | JSX.Element;
  onCollapse?(): void;
  onRemove?(): void;
  wrapperRef?(node: HTMLLIElement): void;
  onSectionChange: (updatedSection: FlattenedItem) => void;
  manual?: DraftManual;
  /** When null/undefined, policy has not been published and section/subsection delete is allowed. */
  policyIssuedAt?: string | null;
}

export const TreeItem = forwardRef<HTMLDivElement, Props>(
  (
    {
      childCount,
      clone,
      depth,
      disableSelection,
      disableInteraction,
      ghost,
      handleProps,
      indentationWidth,
      indicator,
      collapsed,
      onCollapse,
      onRemove,
      style,
      value,
      wrapperRef,
      onSectionChange,
      item,
      manual,
      policyIssuedAt,
      ...props
    },
    ref,
  ) => {
    const confirm = useConfirm();

    const handleToggleState = () => {
      onSectionChange({
        ...item,
        state: item.state === 'ENABLED' ? 'DISABLED' : 'ENABLED',
      });
    };

    const handleDelete = async () => {
      const isSection = depth === 0;
      const translationKey = isSection ? 'deleteSection' : 'deleteSubsection';

      try {
        await confirm({
          title: t(`draftManuals.policy.${translationKey}.title`),
          description: t(`draftManuals.policy.${translationKey}.message`),
          confirmationText: t(`draftManuals.policy.${translationKey}.confirm`),
          cancellationText: t(`draftManuals.policy.${translationKey}.cancel`),
          confirmationButtonProps: { variant: 'contained', color: 'error' },
          cancellationButtonProps: { variant: 'outlined', color: 'inherit' },
        });

        onSectionChange({
          ...item,
          isDeleted: true,
        });
      } catch (error) {
        // User cancelled the deletion
      }
    };

    // Determine if this is agency content by checking for jurisdiction IDs
    // Sections (depth === 0) use jurisdictionSectionId, subsections (depth === 1) use jurisdictionSubSectionId
    const isAgencyContent =
      depth === 0
        ? !item.jurisdictionSectionId || item.jurisdictionSectionId === ''
        : !item.jurisdictionSubSectionId || item.jurisdictionSubSectionId === '';

    const isPolicyUnissued = policyIssuedAt == null;
    const showDeleteIcon =
      !clone &&
      manual?.manualLevel === 'AGENCY' &&
      item.state === 'DISABLED' &&
      isAgencyContent &&
      isPolicyUnissued;

    return (
      <ListItem
        className={classNames(
          styles.Wrapper,
          clone && styles.clone,
          ghost && styles.ghost,
          indicator && styles.indicator,
          disableSelection && styles.disableSelection,
          disableInteraction && styles.disableInteraction,
        )}
        ref={wrapperRef}
        style={
          {
            paddingTop: '0px',
            paddingBottom: '0px',
            '--spacing': `${indentationWidth * depth}px`,
          } as React.CSSProperties
        }
        disableGutters
        {...props}
      >
        <Box
          className={classNames(styles.TreeItem, {
            [styles.disabled]: item.state === 'DISABLED',
          })}
          ref={ref}
          style={style}
          display='flex'
          alignItems='center'
          paddingY={1}
          paddingX={2}
          borderRadius={1}
          boxShadow={item.state === 'DISABLED' ? 'none' : 1}
          width={!clone ? '100%' : '40%'}
          sx={{
            opacity: item.isDeleted === true ? 0.6 : 1,
          }}
        >
          <Handle {...handleProps} />
          <Typography
            className={styles.Text}
            flexGrow={1}
            noWrap
            sx={{
              textDecoration: item.isDeleted === true ? 'line-through' : 'none',
            }}
          >
            {value}
          </Typography>
          {clone && childCount && childCount > 1 ? (
            <Box
              className={styles.Count}
              component='span'
            >
              {childCount}
            </Box>
          ) : null}
          {!clone && (
            <>
              <IconButton
                size='small'
                color='default'
                onClick={handleToggleState}
              >
                {item.state === 'DISABLED' ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
              {showDeleteIcon && (
                <IconButton
                  size='small'
                  color='error'
                  onClick={handleDelete}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </>
          )}
        </Box>
      </ListItem>
    );
  },
);
